/**
 * Replacing the system that is already there.
 *
 * An airport that buys this platform does not switch one morning. It runs the
 * existing application and this one side by side, screen by screen, and moves
 * each over when the evidence says it has earned it. The platform's backend
 * already holds that machinery: a screen contract that maps a legacy screen
 * onto a public endpoint, a shadow mode that records what both systems
 * answered, an agreement rate, a triage on every difference, a cutover guard
 * that refuses on evidence and states what is missing, and a revert that takes
 * one call.
 *
 * This module is the reading of it. Nothing here decides anything: the
 * platform's own `blocking` list is the authority on whether a screen may go
 * live, and what this file adds is the distance to each condition — how many
 * more observations, how many points of agreement short, how many differences
 * still untriaged — which the guard does not report as numbers and which is
 * the only thing a programme manager actually wants.
 */
import type { SourceRef } from "./decision";
import { t, tn } from "../i18n";

export type ScreenMode = "off" | "shadow" | "live";
export type Verdict = "match" | "differ" | "shape_mismatch" | "not_comparable" | "platform_error";
export type Triage = "platform_wrong" | "legacy_wrong" | "both_right" | "noise";

export const MODE_LABEL: Record<ScreenMode, string> = {
  off: "Not being migrated",
  shadow: "Shadow running",
  live: "Served by the platform",
};

export const VERDICT_LABEL: Record<Verdict, string> = {
  match: "Agreed",
  differ: "Differed",
  shape_mismatch: "Different shape",
  not_comparable: "Not comparable",
  platform_error: "Platform failed",
};

export const TRIAGE_LABEL: Record<Triage, string> = {
  platform_wrong: "The platform was wrong",
  legacy_wrong: "The existing screen was wrong",
  both_right: "Both defensible",
  noise: "Noise",
};

export interface Evidence {
  days: number;
  contractVersion: number;
  observations: number;
  /** observations the contract could actually compare */
  comparable: number;
  byVerdict: Partial<Record<Verdict, number>>;
  /** null when nothing comparable has been observed */
  agreementPct: number | null;
  untriagedDifferences: number;
  triage: Partial<Record<Triage, number>>;
  platformP95Ms: number | null;
}

export interface MigrationScreen {
  id: string;
  key: string;
  name: string;
  mode: ScreenMode;
  legacyRoute?: string;
  owner?: string;
  /** how far the agreement may fall short of 100 and still be accepted */
  tolerancePct: number;
  minObservations: number;
  cutoverAt?: string;
  revertedAt?: string;
  contractVersion: number;
  evidence: Evidence;
  /** the platform's own reasons a cutover would be refused; empty means it would be allowed */
  blocking: string[];
}

export interface MigrationStatus {
  screens: MigrationScreen[];
  byMode: Partial<Record<ScreenMode, number>>;
  total: number;
  /** the platform's own words on what the fraction means */
  note: string;
}

export interface Difference {
  id: number;
  observedAt: string;
  verdict: Verdict;
  requestKey: string;
  legacyRows: unknown[];
  platformRows: unknown[];
  differences: unknown[];
  detail: string;
  triage?: Triage;
  triageNote?: string;
}

export interface Transition {
  fromMode: ScreenMode;
  toMode: ScreenMode;
  at: string;
  reason?: string;
  forced: boolean;
}

export interface ScreenDetail extends MigrationScreen {
  description?: string;
  source: Record<string, unknown>;
  projection: Record<string, string>;
  comparison: Record<string, unknown>;
  history: Transition[];
}

/* ── the distance to a cutover ───────────────────────────────────────────── */

export interface Gap {
  key: "observations" | "agreement" | "untriaged" | "conformance";
  /** what the condition asks for, in words */
  want: string;
  /** where this screen stands */
  have: string;
  met: boolean;
  /** 0 to 1, how far along this condition is; null where it cannot be measured */
  progress: number | null;
}

/** The agreement a screen must reach: 100 less its own tolerance. */
export const required = (s: Pick<MigrationScreen, "tolerancePct">) => 100 - s.tolerancePct;

/**
 * The four conditions, measured.
 *
 * The platform refuses or allows; this says how far off each condition is.
 * The conformance condition is tenant-wide and cannot be measured from a
 * screen alone, so it is read from the platform's blocking list rather than
 * computed — the one place this file defers instead of counting.
 */
export function gaps(s: MigrationScreen): Gap[] {
  const e = s.evidence;
  const need = required(s);
  const conformanceBlocked = s.blocking.some((b) => /conformance/i.test(b));
  return [
    {
      key: "observations",
      want: tn(s.minObservations, "{n} shadow run", "{n} shadow runs"),
      have: t("{recorded} recorded, {comparable} comparable", { recorded: e.observations, comparable: e.comparable }),
      met: e.comparable >= s.minObservations,
      progress: s.minObservations ? Math.min(1, e.comparable / s.minObservations) : 1,
    },
    {
      key: "agreement",
      want: t("{pct}% agreement or better", { pct: need }),
      have: e.agreementPct == null ? t("nothing comparable yet") : `${e.agreementPct}%`,
      met: e.agreementPct != null && e.agreementPct >= need,
      progress: e.agreementPct == null ? null : Math.min(1, e.agreementPct / Math.max(1, need)),
    },
    {
      key: "untriaged",
      want: t("every difference looked at"),
      have: e.untriagedDifferences ? t("{n} not yet triaged", { n: e.untriagedDifferences }) : t("all triaged"),
      met: e.untriagedDifferences === 0,
      progress: e.untriagedDifferences === 0 ? 1 : 0,
    },
    {
      key: "conformance",
      want: t("a conformance run behind it"),
      have: conformanceBlocked ? t("none on record") : t("on record"),
      met: !conformanceBlocked,
      progress: conformanceBlocked ? 0 : 1,
    },
  ];
}

/** Ready when the platform itself would not refuse. The gaps say why, not whether. */
export const ready = (s: MigrationScreen) => s.mode !== "live" && s.blocking.length === 0;

/**
 * One number for a screen's progress towards cutover, for ordering a list.
 * Live is 1. Otherwise it is the mean of the measurable conditions, so a
 * screen with the observations but not the triage reads ahead of one with
 * neither, which is the order somebody works them in.
 */
export function progress(s: MigrationScreen): number {
  if (s.mode === "live") return 1;
  const p = gaps(s)
    .map((g) => g.progress)
    .filter((x): x is number => x != null);
  return p.length ? p.reduce((t, x) => t + x, 0) / p.length : 0;
}

/* ── the programme ───────────────────────────────────────────────────────── */

export interface Action {
  screen: string;
  name: string;
  what: string;
  why: string;
  /** 1 is the thing to do first */
  rank: number;
}

export interface Programme {
  total: number;
  live: number;
  shadow: number;
  off: number;
  /** of the screens declared here — never of the existing application's, which nobody has counted */
  livePct: number;
  /** agreement across every shadow screen, weighted by comparable observations */
  agreementPct: number | null;
  observations: number;
  untriaged: number;
  /** screens the platform would let go live today */
  readyNow: MigrationScreen[];
  /** the slowest p95 among shadow screens, because that is what a user would feel */
  worstP95Ms: number | null;
  actions: Action[];
  note: string;
}

export function programme(status: MigrationStatus): Programme {
  const by = (m: ScreenMode) => status.screens.filter((s) => s.mode === m);
  const live = by("live");
  const shadow = by("shadow");
  const off = by("off");
  const comparable = shadow.reduce((t, s) => t + s.evidence.comparable, 0);
  const matched = shadow.reduce((t, s) => t + (s.evidence.byVerdict.match ?? 0), 0);
  const untriaged = status.screens.reduce((t, s) => t + s.evidence.untriagedDifferences, 0);
  const p95s = shadow.map((s) => s.evidence.platformP95Ms).filter((x): x is number => x != null);
  const readyNow = status.screens.filter(ready);

  /* What to do next, in the order a programme manager would do it: clear the
     triage that is blocking a cutover, cut over what has earned it, then feed
     the screens that are still short of runs. */
  const actions: Action[] = [];
  for (const s of [...status.screens].sort((a, b) => progress(b) - progress(a))) {
    if (s.mode === "live") continue;
    const g = gaps(s);
    const untriagedGap = g.find((x) => x.key === "untriaged")!;
    if (!untriagedGap.met)
      actions.push({
        screen: s.key,
        name: s.name,
        what: tn(s.evidence.untriagedDifferences, "Triage {n} difference", "Triage {n} differences"),
        why: t("a cutover cannot be earned on differences nobody has looked at"),
        rank: 1,
      });
    else if (ready(s))
      actions.push({
        screen: s.key,
        name: s.name,
        what: t("Cut over to the platform"),
        why: t("every condition is met and the guard would allow it"),
        rank: 2,
      });
    else {
      const short = g.filter((x) => !x.met).map((x) => x.want);
      actions.push({
        screen: s.key,
        name: s.name,
        what: s.mode === "off" ? t("Start shadow running") : t("Keep shadowing — {short}", { short: short.join("; ") }),
        why: s.mode === "off" ? t("nothing is recorded while a screen is off") : t("the evidence is not there yet"),
        rank: s.mode === "off" ? 4 : 3,
      });
    }
  }
  actions.sort((a, b) => a.rank - b.rank);

  return {
    total: status.total,
    live: live.length,
    shadow: shadow.length,
    off: off.length,
    livePct: status.total ? Math.round((100 * live.length) / status.total) : 0,
    agreementPct: comparable ? Math.round((1000 * matched) / comparable) / 10 : null,
    observations: status.screens.reduce((t, s) => t + s.evidence.observations, 0),
    untriaged,
    readyNow,
    worstP95Ms: p95s.length ? Math.max(...p95s) : null,
    actions,
    note: status.note,
  };
}

/** Where a figure on this desk came from, for the evidence chips the rest of the platform uses. */
export function migrationRef(s: MigrationScreen): SourceRef {
  return {
    kind: "migration",
    ref: s.key,
    label: t("{name} — {runs} shadow run(s) over {days} days, contract v{version}", {
      name: s.name,
      runs: s.evidence.observations,
      days: s.evidence.days,
      version: s.evidence.contractVersion,
    }),
  };
}
